import { FetchError } from '../../common/api/api';
import { Attachment } from '../../common/attachment/Attachment.interface';
import environment from '../../environments';
import { User } from '../../user/User.interface';
import { FileDto } from '../documents/Document.interface';
import { JiraFieldConfig } from '../jira-server/jira-server.interface';
import { TaskTypeEntity } from '../task-types/TaskType.interface';
import { Task } from '../tasks/Task.interface';
import { WorkflowEntity } from '../workflow/Workflow.interface';

const isBasicOptions = environment.hazardConfig?.isBasicOptions;

export interface HazardEntityConfig {
  visibleJiraFields?: JiraFieldConfig[];
}

export interface HazardCardInfoTable {
  head: React.ReactNode[];
  body: React.ReactNode[][];
}

export interface HazardConfig {
  idPrefix?: string;
  cause?: HazardEntityConfig;
  control?: HazardEntityConfig;
  action?: HazardEntityConfig;
  isBasicOptions?: boolean;
}

export enum HazardSeverity {
  Negligible = 1,
  Marginal = 2,
  Moderate = isBasicOptions ? 2 : 3,
  Severe = isBasicOptions ? 3 : 4,
  Catastrophic = 5
}

export enum HazardProbability {
  Improbable = 1,
  Remote = isBasicOptions ? 1 : 2,
  Occasional = 3,
  Probable = isBasicOptions ? 2 : 4,
  Frequent = isBasicOptions ? 3 : 5
}

export enum HazardRisk {
  VeryLow = 1,
  Low = isBasicOptions ? 1 : 2,
  Medium = isBasicOptions ? 2 : 3,
  High = isBasicOptions ? 3 : 4
}

export enum HazardBoardColumnEntity {
  Cause = 'Cause',
  Control = 'Control',
  Action = 'Action'
}
export interface Hazard {
  id: number;
  taskId: number;
  productId: number;
  initialSeverity: HazardSeverity;
  initialProbability: HazardProbability;
  initialRpn: number;
  initialRisk: HazardRisk;
  initialRiskLabel: string;
  residualSeverity: HazardSeverity;
  residualProbability: HazardProbability;
  residualRpn: number;
  residualRisk: HazardRisk;
  residualRiskLabel: string;
  benefitRiskAnalysis: string;
  comments: string;
  riskJustification: string;
  causeTemplate?: FileDto;
  controlTemplate?: FileDto;
  actionTemplate?: FileDto;
  traceabilityTemplate?: FileDto;
  eClinicalTemplate?: FileDto;
  causes?: HazardCause[];
  task: Task;
  name: string;
  title: string;
  category: string;
  otherCategory: string;
  description: string;
  impact: string;
  version: number;
  hazardOwner: User;
  techLead: User;
  created: Date;
  createdBy: User;
  modified: Date;
  modifiedBy: User;
  deleted: boolean;
}

export interface HazardVersion
  extends Omit<Hazard, 'causes' | 'modified' | 'modifiedBy'> {
  fileIds: number[];
  userNeedIds: number[];
  designInputIds: number[];
}

export interface HazardCause {
  id: number;
  title: string;
  version: number;
  description: string;
  initialHarm: HazardProbability;
  initialRisk: HazardRisk;
  initialRiskLabel: string;
  residualHarm: HazardProbability;
  residualRisk: HazardRisk;
  residualRiskLabel: string;
  taskId: number;
  task: Task;
  hazardId: number;
  hazard: Hazard;
  controls: HazardControl[];
  userId: number;
  user: User;
  createdBy: User;
  created: Date;
  modified: Date;
  modifiedBy: User;
  deleted: boolean;
}

export interface HazardCauseVersion {
  id: number;
  version: number;
  title: string;
  description: string;
  initialHarm: HazardProbability;
  initialRisk: HazardRisk;
  residualHarm: HazardProbability;
  residualRisk: HazardRisk;
  attachments?: Attachment[];
  controlIds: number[];
  created: Date;
  task?: Task;
}
export interface HazardControl {
  id: number;
  version: number;
  title: string;
  description: string;
  taskId: number;
  task: Task;
  controlVerification: string;
  hazardId: number;
  hazard: Hazard;
  actions: HazardAction[];
  userId: number;
  user: User;
  createdBy: User;
  created: Date;
  modified: Date;
  modifiedBy: User;
  deleted: boolean;
}

export interface HazardControlVersion {
  id: number;
  version: number;
  title: string;
  description: string;
  attachments?: Attachment[];
  actions: number[];
  created: Date;
  task?: Task;
}
export interface HazardAction {
  id: number;
  title: string;
  version: number;
  description: string;
  taskId: number;
  task: Task;
  hazardId: number;
  hazard: Hazard;
  userId: number;
  user: User;
  createdBy: User;
  created: Date;
  modified: Date;
  modifiedBy: User;
  deleted: boolean;
}
export interface HazardActionVersion {
  id: number;
  version: number;
  title: string;
  description: string;
  attachments?: Attachment[];
  created: Date;
  task?: Task;
}

export enum HazardExportEntity {
  Cause = 'Cause',
  Control = 'Control',
  Action = 'Action',
  TraceabilityReport = 'TraceabilityReport',
  eClinicalReport = 'eClinicalReport'
}

export type HazardReferenceTarget =
  | 'causes'
  | 'controls'
  | 'actions'
  | 'user-needs'
  | 'design-inputs'
  | 'design-outputs'
  | 'verifications'
  | 'validations';

export type HazardReferenceSource =
  | 'causes'
  | 'controls'
  | 'actions'
  | undefined;

export type HazardEntity = HazardCause | HazardControl | HazardAction;

export const HazardWorkflowEntities = [
  WorkflowEntity.Cause,
  WorkflowEntity.Control,
  WorkflowEntity.Action
];

export const HazardTaskTypeEntities = [
  TaskTypeEntity.Cause,
  TaskTypeEntity.Control,
  TaskTypeEntity.Action
];

export interface BaseHazardCardProps {
  hazard: Hazard;
  hazardVersion?: HazardVersion;
  onChange?: (hazard: Hazard) => void;
}

export type BaseHazardBoardColumnProps<
  HazardEntityType extends HazardEntity,
  ValueKey extends string = never
> = { [key in ValueKey]: HazardEntityType | Partial<HazardEntityType> } & {
  hazard: Hazard;
  collapsed?: boolean;
  onCancel?: () => void;
  onError?: () => void;
  onSave?: (value: HazardEntityType) => void;
};

type EditableCardProps<
  ValueType,
  ValueVersionType,
  ValueKey extends string = never,
  ValueVersionKey extends string = never,
  ExtraProps extends object = {}
> = ExtraProps & { [key in ValueKey]: ValueType } & {
  [key in ValueVersionKey]?: ValueVersionType;
} & {
  title?: string;
  onChange?: (value: Partial<ValueType>) => void;
  onUpdate?: () => void;
  onError?: (error: FetchError) => void;
};
export interface HazardCardCommonProps {
  hazardId: number;
  hidden?: boolean;
  enableTitleRedirect?: boolean;
}

export type BaseHazardCausesCardProps = EditableCardProps<
  HazardCause,
  HazardCauseVersion,
  'hazardCause',
  'hazardCauseVersion',
  HazardCardCommonProps
>;
export type BaseHazardControlsCardProps = EditableCardProps<
  HazardControl,
  HazardControlVersion,
  'hazardControl',
  'hazardControlVersion',
  HazardCardCommonProps
>;
export type BaseHazardActionsCardProps = EditableCardProps<
  HazardAction,
  HazardActionVersion,
  'hazardAction',
  'hazardActionVersion',
  HazardCardCommonProps
>;

export const hazardSeverity = isBasicOptions
  ? [
      // healthrhythms environment specific options
      { value: HazardSeverity.Negligible, label: 'Negligible' },
      { value: HazardSeverity.Moderate, label: 'Moderate' },
      { value: HazardSeverity.Severe, label: 'Severe' }
    ]
  : [
      { value: HazardSeverity.Negligible, label: 'Negligible' },
      { value: HazardSeverity.Marginal, label: 'Marginal' },
      { value: HazardSeverity.Moderate, label: 'Moderate' },
      { value: HazardSeverity.Severe, label: 'Severe' },
      { value: HazardSeverity.Catastrophic, label: 'Catastrophic' }
    ];

export const hazardProbability = isBasicOptions
  ? [
      // healthrhythms environment specific options
      { value: HazardProbability.Remote, label: 'Remote' },
      { value: HazardProbability.Probable, label: 'Probable' },
      { value: HazardProbability.Frequent, label: 'Frequent' }
    ]
  : [
      { value: HazardProbability.Improbable, label: 'Improbable' },
      { value: HazardProbability.Remote, label: 'Remote' },
      { value: HazardProbability.Occasional, label: 'Occasional' },
      { value: HazardProbability.Probable, label: 'Probable' },
      { value: HazardProbability.Frequent, label: 'Frequent' }
    ];

export const hazardRisk = [
  { value: HazardRisk.VeryLow, label: 'Very Low' },
  { value: HazardRisk.Low, label: 'Low' },
  { value: HazardRisk.Medium, label: 'Medium' },
  { value: HazardRisk.High, label: 'High' }
];

export const HazardSeverityTable: HazardCardInfoTable = {
  head: [
    'Severity Level',
    'Scale',
    'Description of Harm in terms of Health and Safety'
  ].concat(
    isBasicOptions
      ? []
      : ['Description of Harm in terms of Damage to Property or Environment']
  ),
  body: isBasicOptions
    ? [
        [
          'Negligible',
          '1',
          'Issue does not pose a risk to user, service or poses a data integrity risk.'
        ],
        [
          'Moderate',
          '2',
          'Issue has potential or confirmed negative impact to user, intended use or service or poses a data integrity risk.'
        ],
        [
          'Severe',
          '3',
          'Adverse event may or has occured that may or has caused harm to user or resulted in a data breach.'
        ]
      ]
    : [
        [
          'Negligible',
          '1',
          'No adverse health effects to patient and/or operator; Minor nuisance or inconvenience',
          'Little to no damage to property or environment'
        ],
        [
          'Marginal',
          '2',
          'Transient, self-limiting illness or injury to patient and/or operator',
          'Slight damage to property or environment'
        ],
        [
          'Moderate',
          '3',
          'Significant injury/pain but temporary/reversible to patient and/or operator',
          'Significant damage to property or environment but temporary/reversible'
        ],
        [
          'Severe',
          '4',
          'Serious injury which requires surgical intervention or causes irreversible harm to patient and/or operator',
          'Serious irreversible damage to property or environment'
        ],
        [
          'Catastrophic',
          '5',
          'Death or probable death to patient and/or operator',
          'Major incident that will have significant consequences, lasting effects to property or environment'
        ]
      ]
};

export const CauseProbabilityTable: HazardCardInfoTable = {
  head: ['Probability of Occurrence', 'Scale'].concat(
    isBasicOptions
      ? ['Process Probability of Failure']
      : ['Probability of Occurrence', 'Process Probability of Failure']
  ),
  body: isBasicOptions // healthrhythms environment specific options
    ? [
        [
          'Remote',
          '1',
          'Failure is unlikely or no reported failure of evaluated issue.'
        ],
        [
          'Probable',
          '2',
          'Issue is likely to occur or has occurred frequently.'
        ],
        ['Frequent', '3', 'Issue is systemic or issue is certain to occur.']
      ]
    : [
        [
          'Improbable',
          '1',
          'Almost certain not to occur',
          'Failure is unlikely, No failure ever associated with almost identical processes.'
        ],
        [
          'Remote',
          '2',
          'Unlikely but possible to occur',
          'Very low: Only isolated failure associated with almost identical processes.'
        ],
        [
          'Occasional',
          '3',
          'Moderate probability of occurrence',
          'Low: Isolated failure associated with similar processes.'
        ],
        [
          'Probable',
          '4',
          'Likely to occur',
          'Moderate: Generally associated with processes similar to previous processes which have experienced occasional failure, but not in major proportions.'
        ],
        [
          'Frequent',
          '5',
          'Likely to occur repeatedly',
          'High: Generally associated with processes similar to previous processes that have often failed.'
        ]
      ]
};

export const HazardCategoryOptions = isBasicOptions
  ? [
      // healthrhythms environment specific options
      'Functional',
      'Use Error',
      'Labeling',
      'Use Instructions',
      'Warnings/Escalations',
      'Data'
    ]
  : [
      'Energy - Electric',
      'Energy - Electromagnetic',
      'Energy - Thermal',
      'Energy - Mechanical',
      'Energy - Radiation',
      'Data',
      'Physical',
      'Biological Agent',
      'Chemical Agent',
      'Biocompatibility',
      'Operational - Functional',
      'Operational - Use Error',
      'Information - Labeling',
      'Information - Use Instructions',
      'Information - Warnings',
      'Disposal',
      'Other'
    ];

export const HazardSeverityLabel: Partial<
  Record<HazardSeverity, keyof typeof HazardSeverity>
> = isBasicOptions
  ? {
      // healthrhythms environment specific options
      [HazardSeverity.Negligible]: 'Negligible',
      [HazardSeverity.Moderate]: 'Moderate',
      [HazardSeverity.Severe]: 'Severe'
    }
  : {
      [HazardSeverity.Negligible]: 'Negligible',
      [HazardSeverity.Marginal]: 'Marginal',
      [HazardSeverity.Moderate]: 'Moderate',
      [HazardSeverity.Severe]: 'Severe',
      [HazardSeverity.Catastrophic]: 'Catastrophic'
    };

export const HazardProbabilityLabel: Partial<
  Record<HazardProbability, keyof typeof HazardProbability>
> = isBasicOptions
  ? {
      // healthrhythms environment specific options
      [HazardProbability.Remote]: 'Remote',
      [HazardProbability.Probable]: 'Probable',
      [HazardProbability.Frequent]: 'Frequent'
    }
  : {
      [HazardProbability.Improbable]: 'Improbable',
      [HazardProbability.Remote]: 'Remote',
      [HazardProbability.Occasional]: 'Occasional',
      [HazardProbability.Probable]: 'Probable',
      [HazardProbability.Frequent]: 'Frequent'
    };

export interface HazardBoardData {
  hazardId: number;
  referencedHazardId?: number;
  referencedHazardTitle?: string;
  causes: HazardCause[];
  controls: HazardControl[];
  actions: HazardAction[];
}

export interface ProductHazardBoardData {
  productId: number;
  hazards: Hazard[];
  causes: HazardCause[];
  controls: HazardControl[];
  actions: HazardAction[];
}

export interface HazardSection {
  file?: FileDto | null;
  entity: HazardExportEntity;
}

export const hazardRuleMatrix = new Map([
  [
    HazardSeverity.Negligible,
    new Map([
      [HazardProbability.Frequent, HazardRisk.Medium],
      [HazardProbability.Probable, HazardRisk.Low],
      [HazardProbability.Occasional, HazardRisk.Low],
      [HazardProbability.Remote, HazardRisk.VeryLow],
      [HazardProbability.Improbable, HazardRisk.VeryLow]
    ])
  ],
  [
    HazardSeverity.Marginal,
    new Map([
      [HazardProbability.Frequent, HazardRisk.Medium],
      [HazardProbability.Probable, HazardRisk.Medium],
      [HazardProbability.Occasional, HazardRisk.Low],
      [HazardProbability.Remote, HazardRisk.Low],
      [HazardProbability.Improbable, HazardRisk.VeryLow]
    ])
  ],
  [
    HazardSeverity.Moderate,
    new Map([
      [HazardProbability.Frequent, HazardRisk.High],
      [HazardProbability.Probable, HazardRisk.Medium],
      [HazardProbability.Occasional, HazardRisk.Medium],
      [HazardProbability.Remote, HazardRisk.Low],
      [HazardProbability.Improbable, HazardRisk.Low]
    ])
  ],
  [
    HazardSeverity.Severe,
    new Map([
      [HazardProbability.Frequent, HazardRisk.High],
      [HazardProbability.Probable, HazardRisk.High],
      [HazardProbability.Occasional, HazardRisk.Medium],
      [HazardProbability.Remote, HazardRisk.Medium],
      [HazardProbability.Improbable, HazardRisk.Low]
    ])
  ],
  [
    HazardSeverity.Catastrophic,
    new Map([
      [HazardProbability.Frequent, HazardRisk.High],
      [HazardProbability.Probable, HazardRisk.High],
      [HazardProbability.Occasional, HazardRisk.High],
      [HazardProbability.Remote, HazardRisk.Medium],
      [HazardProbability.Improbable, HazardRisk.Medium]
    ])
  ]
]);
